import { Entity } from '@neptune/shared/entity-domain';
import { AttributeDefinition } from 'domain/experiment/attribute';
import { Widget } from './core/widget';
import {
  AttributesWidgetSource,
  NamespacesWidgetSource,
  WidgetSource,
  WidgetWithAttributesSources,
  WidgetWithNamespaceSources,
  WidgetWithSources,
} from './widget-rendering-model';

export function inferWidgetName(widget: Widget): string {
  if (widget.name) {
    return widget.name;
  }

  return widget.attributes?.[0]?.name || widget.namespaces?.[0] || 'Unnamed';
}

export function inferWidgetWithSourcesName(widget: WidgetWithSources): string {
  if (widget.name) {
    return widget.name;
  }

  let name = '';

  if (isWidgetWithAttributesSources(widget)) {
    name = widget.sources[0]?.attributes[0]?.name;
  }

  if (isWidgetWithNamespaceSources(widget)) {
    name = widget.sources[0]?.namespaces[0];
  }

  return name || 'Unnamed';
}

export function getWidgetSources(
  attributes: AttributeDefinition[] | undefined,
  namespaces: string[] | undefined,
  entities: Entity[],
) {
  if (attributes?.length) {
    return entities.map(({ id, type }) => ({
      attributes,
      entityId: id,
      entityType: type,
    }));
  }

  if (namespaces?.length) {
    return entities.map(({ id, type }) => ({
      namespaces,
      entityId: id,
      entityType: type,
    }));
  }

  return [];
}

export function isWidgetWithAttributesSources(
  widget: WidgetWithSources,
): widget is WidgetWithAttributesSources {
  if (widget.sources.length === 0) {
    return false;
  }

  return (widget.sources as WidgetSource[]).every((source: WidgetSource) =>
    isAttributesWidgetSource(source),
  );
}

export function isAttributesWidgetSource(source: WidgetSource): source is AttributesWidgetSource {
  return source.hasOwnProperty('attributes');
}

export function isWidgetWithNamespaceSources(
  widget: WidgetWithSources,
): widget is WidgetWithNamespaceSources {
  if (widget.sources.length === 0) {
    return false;
  }

  return (widget.sources as WidgetSource[]).every((source: WidgetSource) =>
    isNamespaceWidgetSource(source),
  );
}

export function isNamespaceWidgetSource(source: WidgetSource): source is NamespacesWidgetSource {
  return source.hasOwnProperty('namespaces');
}
